import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, Trash2, ShieldCheck } from "lucide-react";
import { API } from "@/config";
import { useAuth } from "@/context/AuthProvider";

/**
 * Manage Saved Cards — customer-facing page that lists every card
 * Stripe currently has attached to this booker and lets them remove
 * the ones they don't want on file anymore.
 *
 * Data source: GET /my/saved-cards (Stripe-backed, source of truth).
 * DELETE /my/saved-cards/{pm_id} detaches the card from the Stripe
 * Customer so it can no longer be charged off-session. We deliberately
 * do NOT support adding a card from this page — new cards get added
 * naturally when the customer books a rental (Stripe checkout saves
 * the card via setup_future_usage).
 */
export const SavedCards = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState({});
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/my/saved-cards`);
      setRows(data?.cards || []);
    } catch {
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (pm_id) => {
    setBusy((b) => ({ ...b, [pm_id]: true }));
    try {
      await axios.delete(`${API}/my/saved-cards/${pm_id}`);
      toast.success("Card removed.");
      setConfirming(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove that card.");
    } finally {
      setBusy((b) => ({ ...b, [pm_id]: false }));
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen text-[#F6F1E4] py-8 px-5">
      <div className="max-w-2xl mx-auto">
        <Link to="/account" className="inline-flex items-center gap-2 text-xs text-[#B9C8D5] hover:text-[#FFD166] mb-6" data-testid="saved-cards-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to account
        </Link>
        <div className="glass-warm p-6 sm:p-8">
          <div className="eyebrow text-[#FFD166]">Payment methods</div>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1">Saved cards</h1>
          <p className="text-sm text-[#B9C8D5] mt-2">
            These are the cards Stripe has on file for you. We use them to auto-charge approved date changes and add-ons so you don't have to re-enter card info every time.
          </p>

          <div className="mt-6 flex items-center gap-2 text-xs text-emerald-300/90">
            <ShieldCheck className="w-3.5 h-3.5" /> Only the last 4 digits are visible — full card numbers stay on Stripe's servers.
          </div>

          {rows === null && <div className="mt-6 text-[#B9C8D5]">Loading…</div>}
          {rows !== null && rows.length === 0 && (
            <div className="mt-6 p-6 rounded-xl border border-[#FFD166]/20 bg-black/25 text-center text-sm text-[#B9C8D5]" data-testid="saved-cards-empty">
              You don't have any cards on file yet. The next card you use at checkout will show up here so you can reuse it for future changes.
            </div>
          )}
          {rows !== null && rows.length > 0 && (
            <div className="mt-6 space-y-3" data-testid="saved-cards-list">
              {rows.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-4 p-4 rounded-xl border border-[#FFD166]/20 bg-black/25"
                  data-testid={`saved-card-${c.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#FFD166]/10 border border-[#FFD166]/30 grid place-items-center shrink-0">
                    <CreditCard className="w-4 h-4 text-[#FFD166]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#F6F1E4]">
                      {(c.brand || "Card").toUpperCase()} · <span className="tracking-widest text-[#B9C8D5]">•••• {c.last4}</span>
                    </div>
                    <div className="text-[11px] text-[#B9C8D5]">
                      {c.exp_month && c.exp_year && (
                        <>Expires {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</>
                      )}
                      {c.is_default && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#FFD166]">Default</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => setConfirming(c)}
                    disabled={busy[c.id]}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-60"
                    data-testid={`saved-card-remove-${c.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-[100] bg-[#040A0F]/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setConfirming(null)}>
          <div className="glass max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow text-[#FFD166]">Remove card</div>
            <h3 className="font-serif text-xl mt-1">
              {(confirming.brand || "Card").toUpperCase()} •••• {confirming.last4}
            </h3>
            <p className="text-sm text-[#B9C8D5] mt-3">
              We won't be able to auto-charge this card for future date changes or add-ons. You'll still be able to pay via a Stripe link. This won't affect any existing paid reservations.
            </p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setConfirming(null)} className="flex-1 py-2.5 rounded-full border border-[#FFD166]/30 text-[#F6F1E4] text-sm" data-testid="saved-cards-confirm-cancel">Keep it</button>
              <button
                onClick={() => remove(confirming.id)}
                disabled={busy[confirming.id]}
                className="flex-1 py-2.5 rounded-full bg-red-500/85 hover:bg-red-500 text-[#F6F1E4] text-sm font-medium disabled:opacity-60"
                data-testid="saved-cards-confirm-remove"
              >
                {busy[confirming.id] ? "…" : "Remove card"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedCards;
